// Meta (Facebook & Instagram) – OAuth-Verbindung, Seitenwahl, Live-Status
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Facebook, Instagram, Loader2, Unplug, RefreshCw, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/meta`;

export function MetaConnectCard({ flash, onChanged }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const load = async () => setSt((await axios.get(`${API}/status`)).data);
  useEffect(() => {
    load();
    const q = new URLSearchParams(window.location.search).get("meta");
    if (q) {
      const txt = { connected: "✓ Facebook verbunden", denied: "Facebook-Verbindung abgelehnt", invalid_state: "Verbindung abgelaufen – bitte erneut starten", error: "Facebook-Verbindung fehlgeschlagen (Details im Audit-Log)" }[q];
      if (txt) flash(txt);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); if (ok) flash(ok(r)); await load(); onChanged && onChanged(); }
    catch (e) { flash(e.response?.data?.detail || "Meta-Fehler"); }
    finally { setBusy(""); }
  };
  const connect = async () => {
    setBusy("connect");
    try { const r = await axios.get(`${API}/connect`); window.location.assign(r.data.url); }
    catch (e) { flash(e.response?.data?.detail || "Verbindung konnte nicht gestartet werden"); setBusy(""); }
  };
  if (!st) return null;
  return (
    <div className="card !p-4 space-y-3 border-[#1877F2]/30" data-testid="meta-connect-card">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold text-sm flex items-center gap-2"><Facebook size={14} className="text-[#1877F2]" /><Instagram size={14} className="text-pink-400" /> Meta Business (Facebook & Instagram)</div>
          <div className="text-[10px] font-mono text-eb-muted">Graph API · OAuth · App {st.app_id || "–"}</div>
        </div>
        <span className="channel-pill" data-testid="meta-status" style={{ background: st.connected ? "#10B98122" : "#9CA3AF22", color: st.connected ? "#10B981" : "#9CA3AF" }}>
          {st.connected ? `verbunden · ${st.user}` : st.configured ? "nicht verbunden" : "App fehlt"}
        </span>
      </div>
      {!st.connected && (
        <>
          <button className="btn btn-primary w-full justify-center" onClick={connect} disabled={!st.configured || !!busy} data-testid="meta-connect-btn">
            {busy === "connect" ? <Loader2 size={14} className="animate-spin" /> : <Facebook size={14} />} Mit Facebook verbinden
          </button>
          <div className="text-[10px] text-eb-muted space-y-1">
            <div>Du loggst dich selbst bei Facebook ein und bestätigst die Berechtigungen – wir erhalten nur ein widerrufbares Token, nie dein Passwort.</div>
            <div className="flex items-center gap-1">Redirect-URI in der Meta-App eintragen:
              <code className="font-mono text-[10px] bg-black/30 px-1 rounded truncate" data-testid="meta-redirect-uri">{st.redirect_uri}</code>
              <button className="btn !py-0 !px-1" onClick={() => { navigator.clipboard.writeText(st.redirect_uri); flash("✓ Redirect-URI kopiert"); }} data-testid="meta-copy-redirect"><Copy size={10} /></button>
            </div>
          </div>
        </>
      )}
      {st.connected && (
        <>
          <div className="space-y-1" data-testid="meta-pages">
            {st.pages.length === 0 && <div className="text-xs text-amber-300">Keine Facebook-Seiten gefunden – prüfe die Seitenrollen und die App-Berechtigung „pages_show_list“.</div>}
            {st.pages.map((p) => (
              <label key={p.id} className="eb-surface rounded-md px-2 py-1.5 flex items-center gap-2 text-xs cursor-pointer" data-testid={`meta-page-${p.id}`}>
                <input type="radio" name="meta-default-page" checked={st.default_page_id === p.id}
                  onChange={() => run("page", () => axios.put(`${API}/default-page`, { page_id: p.id }), () => `✓ Standard-Seite: ${p.name}`)} data-testid={`meta-page-select-${p.id}`} />
                <div className="min-w-0">
                  <div className="font-medium truncate">{p.name} <span className="text-eb-muted font-mono">· {p.id}</span></div>
                  <div className="text-eb-muted truncate">{p.category || "Seite"}{p.instagram_id ? ` · Instagram @${p.instagram_username || p.instagram_id}` : " · kein Instagram-Business-Konto verknüpft"}</div>
                </div>
              </label>
            ))}
          </div>
          <div className="flex items-center justify-between gap-2 text-[10px] text-eb-muted">
            <span>Token gültig bis {st.expires_at ? st.expires_at.slice(0, 10) : "–"}</span>
            <div className="flex gap-1">
              <button className="btn !py-1 !px-2 text-xs" onClick={() => run("refresh", () => axios.post(`${API}/refresh-pages`), (r) => `✓ ${r.data.pages} Seiten aktualisiert`)} disabled={!!busy} data-testid="meta-refresh-pages">
                {busy === "refresh" ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              </button>
              <button className="btn !py-1 !px-2 text-xs text-rose-400" onClick={() => window.confirm("Facebook-Verbindung trennen?") && run("dc", () => axios.post(`${API}/disconnect`), () => "Facebook getrennt")} disabled={!!busy} data-testid="meta-disconnect">
                <Unplug size={12} /> Trennen
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
